import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { unwrapResult } from "@reduxjs/toolkit";
import { addAlert, selectLoading } from "src/modules/notification/redux";
import {
  checkSnAssignAvailability,
  resetProductDropdown,
  resetSNAvailabilityList,
  updateAssign,
} from "src/modules/companies/redux";
import AssignPanelComponent from "./assignPanel.component";
import ReassignResultDialog from "../../dialogs/reassign-result-dialog";
import ReassignHistoryDialog from "../../dialogs/reassign-history-dialog";

export default function AssignPanelContainer({
  selectedCompany,
  onSerialNumberAssignDialogCloseClick,
}) {
  const dispatch = useDispatch();
  const isUpdating = useSelector((state) =>
    selectLoading(state, updateAssign.typePrefix)
  );
  const isSNexceedRange = useSelector(
    (state) => state.companies.snAvailabilityList.isSNexceedRange
  );
  const snAvailabilityList = useSelector(
    (state) => state.companies.snAvailabilityList.data
  );

  const [isBackdropOpen, setIsBackdropOpen] = useState(false);
  const [reassignResult, setReassignResult] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const formRef = useRef();

  const handleOpenResultDialog = (value) => {
    setReassignResult(value);
  };

  const resetAvailabilityList = () => {
    dispatch(resetSNAvailabilityList());
  };

  const handleSubmit = (formValue) => {
    const { branch, product, format, forceReassign } = formValue;

    setIsBackdropOpen(true);

    // seq number grouping
    const newRanges = [];
    let currentRange = null;
    snAvailabilityList
      .filter((range) => {
        if (range.product) {
          return range.product.id !== product.product_uuid;
        }
        return true;
      })
      .map((range) => ({
        start: range.from_seq_num,
        end: range.to_seq_num,
      }))
      .forEach((r) => {
        if (!currentRange) {
          currentRange = { ...r };
        } else if (currentRange.end + 1 === r.start) {
          currentRange.end = r.end;
        } else {
          newRanges.push(currentRange);
          currentRange = { ...r };
        }
      });
    if (currentRange) {
      newRanges.push(currentRange);
    }

    dispatch(
      updateAssign({
        branchUuid: branch.node_uuid,
        productUuid: product.product_uuid,
        format,
        ranges: newRanges,
        forceReassign: !!forceReassign,
      })
    )
      .then(unwrapResult)
      .then((value) => {
        handleOpenResultDialog(value);
        dispatch(
          addAlert({
            severity: "success",
            message: "Serial Number assigned successfully",
          })
        );
      })
      .catch((error) => {
        // Optionally, you can show a custom alert here or just swallow the error
        // dispatch(addAlert({ severity: "error", message: error.message }));
      })
      .finally(() => {
        setIsBackdropOpen(false);
      });
  };

  const checkSNAvailability = (formik, ranges = null) => {
    dispatch(
      checkSnAssignAvailability({
        branchUuid: formik.values.branch.node_uuid,
        format: formik.values.format,
        ranges,
        isSn: formik.values.generateRangeType === "serial_number",
      })
    );

    const tableView = document.getElementById("preview-table");
    if (tableView) {
      tableView.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest",
      });
    }

    formik.setFieldValue("preview", true);
  };

  useEffect(
    () => () => {
      dispatch(resetProductDropdown());
    },
    []
  );

  const viewReassignHistory = () => {};

  return (
    <>
      <AssignPanelComponent
        selectedCompany={selectedCompany}
        onSerialNumberAssignDialogCloseClick={
          onSerialNumberAssignDialogCloseClick
        }
        isUpdating={isUpdating}
        handleSubmit={handleSubmit}
        isBackdropOpen={isBackdropOpen}
        checkSNAvailability={checkSNAvailability}
        isSNexceedRange={isSNexceedRange}
        resetAvailabilityList={resetAvailabilityList}
        formRef={formRef}
        viewReassignHistory={() => setShowHistory(true)}
      />
      <ReassignResultDialog
        result={reassignResult}
        onClose={() => {
          setReassignResult(null);
          resetAvailabilityList();
          formRef.current.resetForm();
        }}
      />
      {!!showHistory && (
        <ReassignHistoryDialog
          isOpen={showHistory}
          handleClose={() => setShowHistory(false)}
          selectedCompany={selectedCompany}
        />
      )}
    </>
  );
}
